import React, { useMemo } from "react";
import { Box, Menu, Checkbox, Button } from "@mui/material";
import "./EyeExamination.scss";
import { useEyeComponentCheckStatus } from "../../../hooks/useEyeComponentCheckStatus";

const Popper = (): React.ReactNode => {
  const { checkStatus, setCheckStatus } = useEyeComponentCheckStatus();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const isAllVisionChecked = useMemo(
    () => Object.values(checkStatus.vision).every(Boolean),
    [checkStatus.vision]
  );

  const handleVisionToggle = () => {
    const newValue = !isAllVisionChecked;
    setCheckStatus((prev) => ({
      ...prev,
      vision: {
        visualAcuity: newValue,
        autoRefraction: newValue,
        refraction: newValue,
        subjectiveRefraction: newValue,
        fieldOfVision: newValue,
        colorVision: newValue
      }
    }));
  };

  const CustomMenuItem = ({
    checked,
    label,
    subItem = false,
    onChange
  }: {
    checked: boolean;
    label: string;
    subItem?: boolean;
    onChange?: () => void;
  }) => (
    <Box className="menuItem" onClick={onChange}>
      <Box className={`${subItem ? "subItem" : ""}`} fontWeight={`${subItem ? "" : 600}`}>
        {label}
      </Box>
      <Checkbox
        checked={checked}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.stopPropagation();
          if (onChange) onChange();
        }}
        inputProps={{ "aria-label": "checkbox" }}
        size="small"
      />
    </Box>
  );

  return (
    <Box style={{ cursor: "pointer", width: "100%" }}>
      <Button
        variant="outlined"
        onClick={handleClick}
        size="small"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1
        }}
      >
        Add/Remove Items
      </Button>

      <Menu
        id="eye-examination"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={closeMenu}
      >
        <CustomMenuItem checked={isAllVisionChecked} label="Vision" onChange={handleVisionToggle} />

        <CustomMenuItem
          checked={checkStatus.vision.visualAcuity}
          subItem
          label="Visual Acuity"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              vision: { ...prev.vision, visualAcuity: !prev.vision.visualAcuity }
            }))
          }
        />

        <CustomMenuItem
          checked={checkStatus.vision.autoRefraction}
          subItem
          label="Auto Refraction"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              vision: { ...prev.vision, autoRefraction: !prev.vision.autoRefraction }
            }))
          }
        />

        <CustomMenuItem
          checked={checkStatus.vision.refraction}
          subItem
          label="Refraction"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              vision: { ...prev.vision, refraction: !prev.vision.refraction }
            }))
          }
        />

        <CustomMenuItem
          checked={checkStatus.vision.subjectiveRefraction}
          subItem
          label="Subjective Refraction"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              vision: { ...prev.vision, subjectiveRefraction: !prev.vision.subjectiveRefraction }
            }))
          }
        />

        <CustomMenuItem
          checked={checkStatus.vision.fieldOfVision}
          subItem
          label="Field Of Vision"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              vision: { ...prev.vision, fieldOfVision: !prev.vision.fieldOfVision }
            }))
          }
        />

        <CustomMenuItem
          subItem
          checked={checkStatus.vision.colorVision}
          label="Color Vision"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              vision: { ...prev.vision, colorVision: !prev.vision.colorVision }
            }))
          }
        />

        <CustomMenuItem
          checked={checkStatus.anteriorSegment}
          label="Anterior Segment"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              anteriorSegment: !prev.anteriorSegment
            }))
          }
        />

        <CustomMenuItem
          checked={checkStatus.posteriorSegment}
          label="Posterior Segment"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              posteriorSegment: !prev.posteriorSegment
            }))
          }
        />

        <CustomMenuItem
          checked={checkStatus.glassPrescription}
          label="Glass Prescription"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              glassPrescription: !prev.glassPrescription
            }))
          }
        />

        <CustomMenuItem
          checked={checkStatus.lensPrescription}
          label="Lens Prescription"
          onChange={() =>
            setCheckStatus((prev) => ({
              ...prev,
              lensPrescription: !prev.lensPrescription
            }))
          }
        />
      </Menu>
    </Box>
  );
};

export default Popper;
